import { createScrollRestoreGuard } from "./scrollRestoreGuard";

export const RESTORE_CATALOG_SCROLL_KEY = "vf_restore_catalog_scroll";
export const RESTORE_CATALOG_ANCHOR_KEY = "vf_restore_catalog_anchor";

export const isScrollRestorePathActive = (
  expectedPath: string | undefined,
  currentPath: string
): boolean => !expectedPath || expectedPath === currentPath;

let lastPageShowPersisted = false;

if (typeof window !== "undefined") {
  window.addEventListener("pageshow", (event) => {
    lastPageShowPersisted = !!event.persisted;
  });
}

// ---------------------------------------------------------------------------
// Anchor data: saved on leave, used on return to find the exact card
// ---------------------------------------------------------------------------
export interface ScrollAnchor {
  /** Product ID of the card closest to viewport center when the user left */
  productId: string;
  /** Index of the card in the product list (secondary lookup) */
  index: number;
  /** Distance from the top of the anchor card to the top of the viewport */
  scrollOffsetWithinCard: number;
  /** Legacy name kept for anchors saved before this change */
  offsetFromCardTop?: number;
  /** Pixel scroll position (fallback only) */
  scrollY: number;
  /** Route path where the anchor was captured */
  path?: string;
  /** Timestamp used to measure return-restore latency */
  capturedAt?: number;
}

// ---------------------------------------------------------------------------
// Save: capture anchor from the current viewport
// ---------------------------------------------------------------------------
export function captureScrollAnchor(): ScrollAnchor | null {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }

  const scrollY = window.scrollY || 0;
  const viewportCenter = window.innerHeight / 2;

  // Find all product grid items that have a data-product-id
  const cards = Array.from(
    document.querySelectorAll("[data-product-id]")
  ) as HTMLElement[];

  if (cards.length === 0) {
    return null;
  }

  // Find the card whose vertical center is closest to the viewport center
  let bestCard: HTMLElement | null = null;
  let bestDistance = Infinity;
  let bestIndex = 0;

  for (let i = 0; i < cards.length; i++) {
    const rect = cards[i].getBoundingClientRect();
    const cardCenter = rect.top + rect.height / 2;
    const distance = Math.abs(cardCenter - viewportCenter);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestCard = cards[i];
      bestIndex = i;
    }
  }

  if (!bestCard) return null;

  const productId = bestCard.getAttribute("data-product-id") || "";
  const cardRect = bestCard.getBoundingClientRect();
  const cardTopY = cardRect.top + scrollY;

  return {
    productId,
    index: bestIndex,
    scrollOffsetWithinCard: scrollY - cardTopY,
    scrollY,
    path: window.location.pathname + window.location.search,
    capturedAt: Date.now(),
  };
}

export function captureScrollAnchorFromElement(
  element: Element | null
): ScrollAnchor | null {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    !element
  ) {
    return null;
  }

  const card = element.closest("[data-product-id]") as HTMLElement | null;
  if (!card) return captureScrollAnchor();

  const cards = Array.from(
    document.querySelectorAll("[data-product-id]")
  ) as HTMLElement[];
  const explicitIndex = Number(card.getAttribute("data-product-index"));
  const index = Number.isFinite(explicitIndex)
    ? explicitIndex
    : Math.max(0, cards.indexOf(card));
  const scrollY = window.scrollY || 0;
  const cardTopY = card.getBoundingClientRect().top + scrollY;

  return {
    productId: card.getAttribute("data-product-id") || "",
    index,
    scrollOffsetWithinCard: scrollY - cardTopY,
    scrollY,
    path: window.location.pathname + window.location.search,
    capturedAt: Date.now(),
  };
}

// ---------------------------------------------------------------------------
// Save anchor to sessionStorage
// ---------------------------------------------------------------------------
export function saveScrollAnchor(anchor: ScrollAnchor | null): void {
  if (typeof sessionStorage === "undefined") return;

  if (!anchor) {
    sessionStorage.removeItem(RESTORE_CATALOG_ANCHOR_KEY);
    return;
  }

  try {
    sessionStorage.setItem(RESTORE_CATALOG_ANCHOR_KEY, JSON.stringify(anchor));
  } catch {
    // Storage full or unavailable — silent fail
  }

  // Also save the pixel value for backward compatibility with default.vue
  sessionStorage.setItem(RESTORE_CATALOG_SCROLL_KEY, String(anchor.scrollY));
}

// ---------------------------------------------------------------------------
// Load anchor from sessionStorage
// ---------------------------------------------------------------------------
export function loadScrollAnchor(): ScrollAnchor | null {
  if (typeof sessionStorage === "undefined") return null;

  try {
    const raw = sessionStorage.getItem(RESTORE_CATALOG_ANCHOR_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ScrollAnchor;
  } catch {
    return null;
  }
}

const emitRestoreMeasurement = (detail: Record<string, any>) => {
  if (typeof window === "undefined") return;

  const payload = {
    event: "catalog_scroll_restore",
    browser: window.navigator?.userAgent || "",
    ...detail,
  };

  window.dispatchEvent(
    new CustomEvent("vf-catalog-scroll-restore", { detail: payload })
  );

  const dataLayer = (window as any).dataLayer;
  if (Array.isArray(dataLayer)) {
    dataLayer.push(payload);
  }
};

// ---------------------------------------------------------------------------
// Restore: anchor-first, pixel-fallback
// ---------------------------------------------------------------------------

/**
 * Attempt to restore scroll position by finding the anchor card in the DOM.
 * If the anchor card is not present, falls back to pixel scroll once.
 *
 * @param anchor  - Saved anchor data (if available)
 * @param options - Restore options
 */
export function restoreScrollByAnchor(
  anchor: ScrollAnchor | null,
  options?: {
    removeKey?: boolean;
    returnType?: string;
    loadedProductCount?: number;
    persisted?: boolean;
    onRestored?: () => void;
    expectedPath?: string;
  }
): void {
  if (typeof window === "undefined") return;

  const { removeKey = true } = options ?? {};
  const expectedPath = options?.expectedPath ?? anchor?.path;
  const guard = createScrollRestoreGuard(expectedPath);
  const startedAt =
    typeof performance !== "undefined" ? performance.now() : Date.now();

  const fallbackScrollY = anchor?.scrollY ?? 0;

  const cleanup = () => {
    guard.dispose();
    if (removeKey && typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(RESTORE_CATALOG_ANCHOR_KEY);
      sessionStorage.removeItem(RESTORE_CATALOG_SCROLL_KEY);
    }
    options?.onRestored?.();
  };

  // No anchor data at all — use legacy pixel fallback
  if (!anchor || !anchor.productId) {
    guard.dispose();
    if (fallbackScrollY > 0) {
      restoreScrollWhenReady(fallbackScrollY, { removeKey, expectedPath });
      window.requestAnimationFrame(() => options?.onRestored?.());
    } else {
      options?.onRestored?.();
    }
    return;
  }

  const measure = (returnType: string, targetY: number, attempts: number) => {
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();

    emitRestoreMeasurement({
      return_type: options?.returnType || returnType,
      time_to_restore_ms: Math.round(now - startedAt),
      scroll_error: Math.round(Math.abs(window.scrollY - targetY)),
      loaded_product_count:
        options?.loadedProductCount ??
        document.querySelectorAll("[data-product-id]").length,
      pageshow_persisted: options?.persisted ?? lastPageShowPersisted,
      anchor_product_id: anchor.productId,
      anchor_index: anchor.index,
      attempts,
    });
  };

  let attempts = 0;
  const maxAttempts = 120;

  // Virtual scrolling renders lightweight placeholder divs that carry
  // data-product-id and a crawl link. Wait for actual card content before
  // measuring the anchor; a hidden SEO link does not make a card ready.
  const isRenderedCard = (el: HTMLElement): boolean =>
    Array.from(el.children).some(
      (child) => !child.hasAttribute("data-ssr-product-link")
    );

  const tryAnchorRestore = () => {
    if (!guard.isActive()) {
      cleanup();
      return;
    }

    attempts++;
    const escapedProductId =
      typeof CSS !== "undefined" && CSS.escape
        ? CSS.escape(anchor.productId)
        : anchor.productId.replace(/"/g, '\\"');
    const card = document.querySelector(
      `[data-product-id="${escapedProductId}"]`
    ) as HTMLElement | null;

    if (card && isRenderedCard(card)) {
      // Found it — restore scroll so the card is at the same viewport offset
      const cardRect = card.getBoundingClientRect();
      const currentCardTop = cardRect.top + window.scrollY;
      const targetScrollY =
        anchor.scrollOffsetWithinCard !== undefined
          ? currentCardTop + anchor.scrollOffsetWithinCard
          : currentCardTop - (anchor.offsetFromCardTop ?? 0);

      window.scrollTo(0, Math.max(0, targetScrollY));
      measure("anchor", targetScrollY, attempts);
      cleanup();
      return;
    }

    // Card not in DOM yet or still a placeholder — try index fallback
    const cards = document.querySelectorAll("[data-product-id]");
    const fallbackCard =
      typeof anchor.index === "number"
        ? (cards[anchor.index] as HTMLElement | undefined)
        : undefined;

    if (fallbackCard && isRenderedCard(fallbackCard)) {
      const currentCardTop =
        fallbackCard.getBoundingClientRect().top + window.scrollY;
      const targetScrollY = currentCardTop + anchor.scrollOffsetWithinCard;

      window.scrollTo(0, Math.max(0, targetScrollY));
      measure("index", targetScrollY, attempts);
      cleanup();
      return;
    }

    if (attempts < maxAttempts) {
      window.requestAnimationFrame(tryAnchorRestore);
      return;
    }

    // Max attempts reached — fall back to pixel scroll or whatever we have
    if (card) {
      // Card exists but never got children — use it anyway as last resort
      const cardRect = card.getBoundingClientRect();
      const currentCardTop = cardRect.top + window.scrollY;
      const targetScrollY =
        anchor.scrollOffsetWithinCard !== undefined
          ? currentCardTop + anchor.scrollOffsetWithinCard
          : currentCardTop - (anchor.offsetFromCardTop ?? 0);
      window.scrollTo(0, Math.max(0, targetScrollY));
      measure("anchor_placeholder", targetScrollY, attempts);
    } else if (fallbackScrollY > 0) {
      window.scrollTo(0, fallbackScrollY);
      measure("pixel", fallbackScrollY, attempts);
    }
    cleanup();
  };

  window.requestAnimationFrame(tryAnchorRestore);
}

// ---------------------------------------------------------------------------
// Legacy pixel-only restore (kept for callers that don't have anchor data,
// e.g. home.vue or default.vue ghost flow)
// ---------------------------------------------------------------------------
export function restoreScrollWhenReady(
  savedScrollY: number,
  options?: {
    removeKey?: boolean;
    tolerance?: number;
    expectedPath?: string;
  }
) {
  if (typeof window === "undefined" || savedScrollY <= 0) return;

  const { removeKey = true } = options ?? {};
  const guard = createScrollRestoreGuard(options?.expectedPath);

  const tryRestore = () => {
    const active = guard.isActive();
    guard.dispose();
    if (!active) {
      if (removeKey && typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem(RESTORE_CATALOG_SCROLL_KEY);
      }
      return;
    }

    window.scrollTo(0, savedScrollY);

    if (removeKey && typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(RESTORE_CATALOG_SCROLL_KEY);
    }
  };

  window.requestAnimationFrame(tryRestore);
}
